import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { SchemeInfoItem, SchemeInfoSection } from "@shared/types/foyer";
import { LocaleField } from "../template/LocalizedSectionsEditor";
import { tokens } from "../../theme";

/**
 * What an own section shows instead of its rows. The model has exactly one
 * kind of embed - `SchemeInfoSection.embedUrl`, a web page the TV frames in
 * place of the rows - so the choice is "none" or "a web page".
 */
type EmbedKind = "none" | "url";

const LABEL_SX = {
  fontSize: 12,
  fontWeight: "fontWeightSemibold",
  color: tokens.ink,
};

/**
 * The form for ONE own section on Information (NendaPortal_1 1689:14148): its
 * title, an optional embedded page, and its label / value rows. Rendered in
 * the page's Add modal and Edit drawer, which own the local copy it edits.
 * Edits one language at a time - `locale` - through the same LocalizedString
 * helpers as the template's section editor.
 */
export default function OwnSectionEditor({
  section,
  locale,
  defaultLocale,
  onChange,
}: {
  section: SchemeInfoSection;
  locale: string;
  defaultLocale: string;
  onChange: (next: SchemeInfoSection) => void;
}) {
  // "Web page" with nothing typed yet is a real state of the form, but not of
  // the data (an empty URL is stored as no URL), so the choice lives here.
  // The surface around it is keyed by section id, so it starts fresh per
  // section.
  const { t } = useTranslation(["templates", "common"]);
  const [kind, setKind] = useState<EmbedKind>(
    section.embedUrl ? "url" : "none"
  );
  const patch = (part: Partial<SchemeInfoSection>) =>
    onChange({ ...section, ...part });
  const setItem = (j: number, part: Partial<SchemeInfoItem>) =>
    patch({
      items: section.items.map((it, k) => (k === j ? { ...it, ...part } : it)),
    });
  const field = { locale, defaultLocale };

  return (
    <Box data-testid="own-section-editor">
      <Box sx={{ mb: 3 }}>
        <Typography sx={LABEL_SX}>{t("common:labels.title")}</Typography>
        <Box sx={{ mt: 1 }}>
          <LocaleField
            value={section.section}
            {...field}
            ariaLabel={t("common:labels.title")}
            testId="own-section-title"
            placeholder={t("ownSectionEditor.titlePlaceholder")}
            onChange={(v) => patch({ section: v })}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography sx={LABEL_SX}>
          {t("ownSectionEditor.embedLabel")}
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
          <TextField
            select
            size="small"
            value={kind}
            onChange={(e) => {
              const next = e.target.value as EmbedKind;
              setKind(next);
              if (next === "none") patch({ embedUrl: undefined });
            }}
            SelectProps={{
              inputProps: { "aria-label": t("ownSectionEditor.embedKindAria") },
            }}
            sx={{ flex: "0 0 47%", minWidth: 0 }}
            data-testid="own-section-embed-kind"
          >
            <MenuItem value="none">{t("ownSectionEditor.embedNone")}</MenuItem>
            <MenuItem value="url">{t("ownSectionEditor.embedUrl")}</MenuItem>
          </TextField>
          <TextField
            size="small"
            fullWidth
            placeholder="https://"
            disabled={kind === "none"}
            value={section.embedUrl ?? ""}
            onChange={(e) =>
              patch({ embedUrl: e.target.value.trim() || undefined })
            }
            inputProps={{
              "aria-label": t("ownSectionEditor.embedUrlAria"),
              "data-testid": "own-section-embed-url",
            }}
          />
        </Stack>
        <Typography variant="meta" sx={{ mt: 1, color: tokens.inkMuted }}>
          {kind === "url"
            ? t("ownSectionEditor.urlHelp")
            : t("ownSectionEditor.noneHelp")}
        </Typography>
      </Box>

      <Typography
        component="h4"
        sx={{ typography: "title", fontWeight: "fontWeightSemibold", mb: 2 }}
        data-testid="own-section-rows-heading"
      >
        {t("ownSectionEditor.rows", { count: section.items.length })}
      </Typography>
      <Stack spacing={2} data-testid="own-section-rows">
        {section.items.map((it, j) => (
          <Stack
            key={j}
            direction="row"
            spacing={2}
            alignItems="center"
            data-testid={`own-section-row-${j}`}
          >
            <LocaleField
              value={it.label}
              {...field}
              ariaLabel={t("ownSectionEditor.rowLabelAria", { n: j + 1 })}
              placeholder={t("ownSectionEditor.labelPlaceholder")}
              onChange={(v) => setItem(j, { label: v })}
            />
            <LocaleField
              value={it.value}
              {...field}
              ariaLabel={t("ownSectionEditor.rowValueAria", { n: j + 1 })}
              placeholder={t("ownSectionEditor.valuePlaceholder")}
              onChange={(v) => setItem(j, { value: v })}
            />
            <IconButton
              aria-label={t("ownSectionEditor.deleteRowAria", { n: j + 1 })}
              onClick={() =>
                patch({ items: section.items.filter((_, k) => k !== j) })
              }
              sx={{ color: tokens.inkMuted, flexShrink: 0 }}
              data-testid={`own-section-row-${j}-delete`}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          endIcon={<AddIcon />}
          onClick={() =>
            patch({ items: [...section.items, { label: "", value: "" }] })
          }
          sx={{
            minWidth: 200,
            textTransform: "none",
            fontWeight: "fontWeightSemibold",
          }}
          data-testid="own-section-add-row"
        >
          {t("ownSectionEditor.addRow")}
        </Button>
      </Stack>
    </Box>
  );
}
